import React, { useState } from "react"
import { useSelector } from "react-redux"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, FilterSegment } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl, formatPeriodLabel } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const inputStyle = { borderRadius: "10px", minHeight: "38px" }

const purchasedOptions = [
	{ id: 1, label: "Закупаемые" },
	{ id: 0, label: "Все позиции" },
]

const writeOffOptions = [
	{ id: 0, label: "Без списаний" },
	{ id: 1, label: "Со списаниями" },
]

function formatQty(value) {
	const number = Number(value || 0)
	if (Math.abs(number) >= 100) return formatMoney(number, 0)
	return number.toFixed(2).replace(/\.?0+$/, "").replace(".", ",")
}

function needStyle(status) {
	if (status === "need_buy") return { background: "var(--dark-tint-danger, #fdecec)", color: "var(--dark-fg-danger, #d23434)" }
	return { background: "var(--dark-tint-success, #e7f8ef)", color: "var(--dark-fg-success, #1f9d57)" }
}

function KpiCard({ title, value, sublabel, icon, color = "#d23434" }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					<div className="d-flex align-items-center justify-content-center" style={{ width: "30px", height: "30px", borderRadius: "10px", background: `${color}18`, color }}>
						<i className={`uil ${icon}`}></i>
					</div>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function RecommendationTable({ items, daysAhead }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
					<div>
						<h5 className="mb-0">Что нужно докупить</h5>
						<div style={mutedStyle}>Расход — фактические списания склада: продажи, сырьё в приготовление и разбор, за вычетом удалений и возвратов</div>
					</div>
					<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "14px", padding: "4px 10px", color: "var(--dark-text-muted, #5b6472)" }}>
						{formatMoney(items.length, 0)} товаров
					</span>
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>#</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Товар</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Поставщик</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Расход за период</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Нужно на {formatMoney(daysAhead, 0)} дн.</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Остаток</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>К закупке</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Хватит на</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Цена</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Сумма</th>
							</tr>
						</thead>
						<tbody>
							{items.length === 0 && (
								<tr><td colSpan="10" className="text-muted text-center py-4">Нет данных по выбранным фильтрам</td></tr>
							)}
							{items.map((item, index) => {
								const borderBottom = index === items.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								const badgeStyle = needStyle(item.status)
								return (
									<tr key={item.product_id}>
										<td className="py-3 text-muted" style={{ borderBottom }}>{index + 1}</td>
										<td className="py-3" style={{ borderBottom }}>
											<div style={{ fontWeight: 700 }}>{item.product_name}</div>
											<div style={mutedStyle}>
												{item.unit_name || "—"} · расход/день {formatQty(item.daily_usage)}
												{!item.is_purchased && " · не закупается"}
											</div>
										</td>
										<td className="py-3 text-muted" style={{ borderBottom }}>{item.supplier_name}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											<div>{formatQty(item.used_quantity)} {item.unit_name}</div>
											<div style={mutedStyle}>{item.used_amount_short}</div>
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatQty(item.required_quantity)} {item.unit_name}</td>
										<td className="py-3 text-end" style={{ borderBottom, color: Number(item.balance) < 0 ? "#d23434" : undefined }}>
											{formatQty(item.balance)} {item.unit_name}
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											<span style={{ ...badgeStyle, borderRadius: "12px", padding: "4px 10px", fontWeight: 800, fontSize: "12px" }}>
												{formatQty(item.recommended_quantity)} {item.unit_name}
											</span>
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											{item.days_left === null ? "—" : `${formatQty(item.days_left)} дн.`}
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											<div>{formatMoney(item.cost_price)}</div>
											<div style={mutedStyle}>
												{item.price_source === "last_purchase"
													? `приход ${item.last_purchase_at ? new Date(item.last_purchase_at.replace(" ", "T")).toLocaleDateString("ru-RU") : ""}`
													: "средняя расхода"}
											</div>
										</td>
										<td className="py-3 text-end" style={{ borderBottom, fontWeight: 800 }}>
											{item.recommended_cost_short}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function RecommendedProducts() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("7days"),
		pos_id: reduxAccount.pos_id ?? -1,
		wms_id: reduxAccount.wms_id ?? -1,
		category_id: -1,
		menu_id: -1,
		supplier_id: -1,
		days_ahead: "",
		only_purchased: 1,
		include_write_off: 0,
	}, searchWithFilter)
	const { draft, applied, patch } = filters
	const options = useAnalyticsOptions(["warehouses", "suppliers", "menus", "categories"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/recommended-products", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	function exportExcel() {
		if (!data) return
		const rows = (data.items || [])
			.map(item => `<tr><td>${item.product_name}</td><td>${item.supplier_name}</td><td>${item.used_quantity}</td><td>${item.required_quantity}</td><td>${item.balance}</td><td>${item.recommended_quantity}</td><td>${item.unit_name || ""}</td><td>${item.cost_price}</td><td>${item.recommended_cost}</td></tr>`)
			.join("")
		const html = `<table border="1"><thead><tr><th>Товар</th><th>Поставщик</th><th>Расход за период</th><th>Нужно на ${data.days_ahead} дн.</th><th>Остаток</th><th>К закупке</th><th>Ед.</th><th>Цена</th><th>Сумма</th></tr></thead><tbody>${rows}</tbody></table>`
		const blob = new Blob([`﻿${html}`], { type: "application/vnd.ms-excel" })
		const link = document.createElement("a")
		link.href = URL.createObjectURL(blob)
		link.download = `recommended-products-${data.start_date}_${data.end_date}.xls`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	const summary = data?.summary || {}
	const items = data?.items || []
	const needItems = items.filter(item => item.status === "need_buy")
	const daysAhead = data?.days_ahead || 0

	return (
		<>
			<AnalyticsFilterBar
				title="Рекомендованные товары"
				icon="uil-shopping-basket"
				subtitle="Что докупить по фактическому расходу склада"
				filters={filters}
				presets={["yesterday", "7days", "14days", "30days", "custom"]}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={exportExcel}>
						<i className="uil uil-file-export me-1"></i>
						Excel
					</button>
				}
			>
				<FilterField label="Филиал">
					<FilterSelect options={options.poses} allLabel="Все филиалы" value={draft.pos_id} onChange={(id) => patch({ pos_id: id, wms_id: -1, supplier_id: -1 })} />
				</FilterField>
				<FilterField label="Склад">
					<FilterSelect options={options.warehouses} allLabel="Все склады" value={draft.wms_id} onChange={(id) => patch({ wms_id: id })} />
				</FilterField>
				<FilterField label="Категория">
					<FilterSelect options={options.categories} allLabel="Все категории" value={draft.category_id} onChange={(id) => patch({ category_id: id })} />
				</FilterField>
				<FilterField label="Меню">
					<FilterSelect options={options.menus} allLabel="Все меню" value={draft.menu_id} onChange={(id) => patch({ menu_id: id })} />
				</FilterField>
				<FilterField label="Поставщик">
					<FilterSelect options={options.suppliers} allLabel="Все поставщики" value={draft.supplier_id} onChange={(id) => patch({ supplier_id: id })} />
				</FilterField>
				<FilterField label="Закупить на, дней">
					<input
						type="number"
						min="1"
						max="365"
						className="form-control"
						style={inputStyle}
						placeholder="как период"
						value={draft.days_ahead}
						onChange={(event) => patch({ days_ahead: event.target.value === "" ? "" : Math.max(1, Math.min(365, Number(event.target.value))) })}
					/>
				</FilterField>
				<FilterField label="Позиции">
					<FilterSegment options={purchasedOptions} value={Number(draft.only_purchased)} onChange={(id) => patch({ only_purchased: id })} />
				</FilterField>
				<FilterField label="Списания в расходе">
					<FilterSegment options={writeOffOptions} value={Number(draft.include_write_off)} onChange={(id) => patch({ include_write_off: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<KpiCard title="К закупке" value={formatMoney(summary.items_to_buy, 0)} sublabel={`из ${formatMoney(summary.items_total, 0)} товаров`} icon="uil-shopping-cart" />
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard title="Сумма закупки" value={summary.total_recommended_cost_short || "0"} sublabel="по цене последнего прихода" icon="uil-money-bill" color="#4c6ef0" />
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title="Горизонт закупки"
						value={`${formatMoney(daysAhead, 0)} дн.`}
						sublabel={`расход/день — по ${data?.period_days || 0} дн. (${formatPeriodLabel(applied.start_date, applied.end_date)})`}
						icon="uil-calendar-alt"
						color="#f59f00"
					/>
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard title="Хватает" value={formatMoney(items.length - needItems.length, 0)} sublabel={`расход за период ${summary.total_used_amount_short || "0"}`} icon="uil-check-circle" color="#1f9d57" />
				</div>
			</div>

			<RecommendationTable items={items} daysAhead={daysAhead} />
		</>
	)
}

export default RecommendedProducts
